'use client';

import * as React from 'react';
import { useTranslations } from 'next-intl';
import { Separator } from '@/components/ui/separator';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import departmentsAndDiseases from '@/data/departments-and-diseases';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { Department } from '@/types';
import { Badge } from '@/components/ui/badge';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

export default function DepartmentsSection() {
  const router = useRouter();
  const t = useTranslations('departments_and_diseases_section');
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 bg-brand rounded-xl p-6 lg:p-10 text-white">
      <div className="lg:col-span-12">
        <CardContent className="p-0">
          <Carousel
            className="w-full"
            setApi={setApi}
            opts={{
              align: 'start',
              loop: true,
            }}
          >
            <CarouselContent className="-ml-4">
              {departmentsAndDiseases.map((department: Department, index) => (
                <CarouselItem
                  key={index}
                  className="
                    pl-4
                    basis-full
                    sm:basis-1/2
                    lg:basis-1/3
                    xl:basis-1/4
                    2xl:basis-1/4
                  "
                >
                  <div className="h-full">
                    <Card className="h-full">
                      <div className="mx-auto text-contrast">
                        {department.icon}
                      </div>
                      <CardHeader>
                        <CardTitle className="text-center text-foreground text-lg">
                          {t(`department_items.${department.slug}`)}
                        </CardTitle>
                      </CardHeader>

                      <Separator className="my-2" />

                      <CardContent className="p-4">
                        <div className="flex flex-col gap-2 max-h-56 overflow-y-auto pr-1">
                          {department.diseases.map((diseaseSlug, diseaseIndex) => (
                            <Badge
                              key={`disease-${diseaseIndex}`}
                              variant="outline"
                              className="w-full justify-start text-left hover:bg-accent hover:cursor-pointer"
                              // onClick={() => router.push(`/diseases/${diseaseSlug}`)}
                            >
                              {t(`disease_items.${diseaseSlug}.name`)}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="mt-4 flex w-full items-center justify-center gap-2">
              {Array.from({ length: count }).map((_, i) => (
                <button
                  key={i}
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => api?.scrollTo(i)}
                  className={[
                    'h-2 w-2 rounded-full transition-all',
                    i === current ? 'w-6 bg-white' : 'bg-white/40 hover:bg-white/60',
                  ].join(' ')}
                />
              ))}
            </div>
          </Carousel>
        </CardContent>
      </div>
    </div>
  );
}
